import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "@/i18n";
import { usePortal } from "@/components/portal/PortalContext";

/**
 * Portal top bar (CE-24) — org identity + plan + the logout control the SPA
 * previously had no home for. Logout hits the same /api/org/auth/logout the
 * old Next top bar used, then routes to the (unguarded) portal login page.
 */
interface PortalTopBarProps {
  /** Whether the sidebar drawer is open below `md` (CE-135). */
  navOpen?: boolean;
  onOpenNav?: () => void;
}

export function PortalTopBar({ navOpen = false, onOpenNav }: PortalTopBarProps) {
  const t = useTranslations("portal.topbar");
  const { user, org } = usePortal();
  const navigate = useNavigate();
  const { locale } = useParams();
  const { pathname, search } = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);
  const activeLocale = locale ?? DEFAULT_LOCALE;

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await fetch("/api/org/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      // best-effort; navigate away regardless
    }
    navigate(`/${locale ?? DEFAULT_LOCALE}/portal/login`);
  }

  return (
    <header className="h-14 bg-surface-1 border-b border-edge px-4 md:px-6 flex items-center justify-between gap-3 shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        {/* Below md the sidebar is a drawer, and this opens it (CE-135). */}
        <button
          type="button"
          onClick={onOpenNav}
          aria-label={t("openMenu")}
          aria-expanded={navOpen}
          aria-controls="portal-sidebar"
          className="md:hidden p-1.5 -ml-1.5 rounded-md text-txt-secondary hover:text-txt-primary hover:bg-surface-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        <h2 className="text-sm font-medium text-txt-secondary truncate">{org.name}</h2>
        <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-accent-500/10 text-accent-600 border border-accent-500/20">
          {org.plan}
        </span>
      </div>
      <div className="flex items-center gap-3 md:gap-4 shrink-0">
        {/* Locale switch — swaps the /:locale segment, keeps the rest of the path */}
        <div
          className="flex items-center rounded-md border border-edge overflow-hidden"
          role="group"
          aria-label={t("languageLabel")}
        >
          {SUPPORTED_LOCALES.map((l) => (
            <Link
              key={l}
              to={pathname.replace(/^\/(en|de)(?=\/|$)/, `/${l}`) + search}
              aria-current={activeLocale === l ? "true" : undefined}
              className={`px-2 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                activeLocale === l
                  ? "bg-accent-500/10 text-accent-600"
                  : "text-txt-muted hover:text-txt-secondary"
              }`}
            >
              {l}
            </Link>
          ))}
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-sm font-medium text-txt-primary">{user.name}</p>
          <p className="text-xs text-txt-muted">{user.email}</p>
        </div>
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          className="text-sm text-txt-muted hover:text-txt-secondary transition-colors disabled:opacity-50"
        >
          {loggingOut ? "…" : t("logout")}
        </button>
      </div>
    </header>
  );
}
